'use client';

import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';

import { cn } from '@/utilities/cn';
import { Svg } from '@/components/ui/svg';
import { CMSLink } from '@/components/Link';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

export const useDotButton = (emblaApi: CarouselApi): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((emblaApi: CarouselApi) => {
    setScrollSnaps(emblaApi!.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: CarouselApi) => {
    setSelectedIndex(emblaApi!.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      return () => {
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    const snaps = api?.scrollSnapList();

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn(
            'relative',
            // snaps && snaps?.length > 1 && 'pb-[72px]',
            className,
          )}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    wrapperClassName?: string;
    overflowVisible?: boolean;
  }
>(({ wrapperClassName, overflowVisible, className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className={cn(wrapperClassName, !overflowVisible && 'overflow-hidden')}
    >
      <div
        ref={ref}
        className={cn(
          'flex',
          // orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel();

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        // orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  );
});
CarouselItem.displayName = 'CarouselItem';

const CarouselPrev: React.FC<
  React.HTMLAttributes<HTMLButtonElement> & {
    ref?: React.Ref<HTMLButtonElement>;
  }
> = ({ className, ref, ...props }) => {
  const { orientation, scrollPrev, canScrollPrev, api } = useCarousel();
  const snaps = api?.scrollSnapList();

  if (!snaps || snaps.length === 1) {
    return null;
  }

  return (
    <button
      ref={ref}
      className={cn(
        'flex-center hover:bg-blue hover:text-whites aspect-square w-10 flex-none cursor-pointer rounded-full border border-gray-500 text-gray-200 transition hover:text-white disabled:opacity-50 md:w-8',
        orientation === 'horizontal' ? '' : '',
        !canScrollPrev && 'pointer-events-none opacity-60',
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <Svg className="md:w-[18px]" id="arrow-left" width="24" height="24" />
      <span className="sr-only">Previous slide</span>
    </button>
  );
};
CarouselPrev.displayName = 'CarouselPrev';

const CarouselNext: React.FC<
  React.HTMLAttributes<HTMLButtonElement> & {
    ref?: React.Ref<HTMLButtonElement>;
  }
> = ({ className, ref, ...props }) => {
  const { orientation, scrollNext, canScrollNext, api } = useCarousel();
  const snaps = api?.scrollSnapList();

  if (!snaps || snaps.length === 1) {
    return null;
  }

  return (
    <button
      ref={ref}
      className={cn(
        'flex-center hover:bg-blue hover:text-whites aspect-square w-10 flex-none cursor-pointer rounded-full border border-gray-500 text-gray-200 transition hover:text-white disabled:opacity-50 md:w-8',
        orientation === 'horizontal' ? '' : '',
        !canScrollNext && 'pointer-events-none opacity-60',
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <Svg className="md:w-[18px]" id="arrow-right" width="24" height="24" />
      <span className="sr-only">Next slide</span>
    </button>
  );
};
CarouselNext.displayName = 'CarouselNext';

const CarouselDots: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    btnClassName?: string;
    ref?: React.Ref<HTMLDivElement>;
  }
> = ({ className, btnClassName, ref, ...props }) => {
  const { api } = useCarousel();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);

  if (scrollSnaps.length < 2) {
    return null;
  }

  return (
    <div ref={ref} className={cn('flex w-full', className)} {...props}>
      {scrollSnaps.map((_, index) => (
        <button
          className={cn(
            'h-0.5 flex-1',
            index === selectedIndex ? 'bg-blue' : 'bg-[#d5d5d5]',
            btnClassName,
          )}
          onClick={() => onDotButtonClick(index)}
          key={index}
        />
      ))}
    </div>
  );
};
CarouselDots.displayName = 'CarouselDots';

export const CarouselControls: React.FC<{
  linkText?: string;
  linkUrl?: string;
}> = ({ linkText, linkUrl }) => {
  return (
    <>
      <CarouselDots className="mt-[30px]" />

      <div
        className={cn(
          'mt-4 flex items-center justify-between gap-4',
          !linkUrl && 'justify-end',
        )}
      >
        <div className="flex gap-2">
          <CarouselPrev />
          <CarouselNext />
        </div>

        {linkUrl && (
          <CMSLink appearance="outline-blue" size="sm" url={linkUrl}>
            {linkText}
          </CMSLink>
        )}
      </div>
    </>
  );
};

CarouselControls.displayName = 'CarouselControls';

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrev,
  CarouselNext,
  CarouselDots,
};
